// src/context/StoreContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { getProductos, getProducto, guardarOrden } from "../config/firebase";

const StoreContext = createContext();

export const useShopify = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useShopify debe usarse dentro de StoreProvider");
  return ctx;
};

export const ShopifyProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cartItems, setCartItems] = useState([]);

  // Persistencia carrito
  useEffect(() => {
    const saved = localStorage.getItem("romi-cart");
    if (saved) {
      try {
        setCartItems(JSON.parse(saved));
      } catch {}
    }
  }, []);

  useEffect(() => {
    cartItems.length > 0
      ? localStorage.setItem("romi-cart", JSON.stringify(cartItems))
      : localStorage.removeItem("romi-cart");
  }, [cartItems]);

  // ── Productos ─────────────────────────────────────────────
  const fetchProducts = useCallback(
    async (limit = 60, page = 1, search = "") => {
      setLoading(true);
      setError(null);
      try {
        let prods = await getProductos();
        if (search) {
          const t = search.toLowerCase();
          prods = prods.filter(
            (p) =>
              p.nombre?.toLowerCase().includes(t) ||
              p.descripcion?.toLowerCase().includes(t),
          );
        }
        // Normalizar al formato que usa la tienda
        const normalized = prods.map(normalizar);
        setProducts(normalized);
        setLoading(false);
        return normalized;
      } catch (err) {
        setError(err.message);
        setLoading(false);
        return [];
      }
    },
    [],
  );

  const getProductByHandle = useCallback(
    async (id) => {
      try {
        // Buscar en cache primero
        const cached = products.find((p) => p.id === id || p.handle === id);
        if (cached) return cached;
        const prod = await getProducto(id);
        return prod ? normalizar(prod) : null;
      } catch (err) {
        throw err;
      }
    },
    [products],
  );

  const getCollectionProducts = useCallback(async (categoria, limit = 20) => {
    try {
      const prods = await getProductos(
        categoria === "frontpage" ? null : categoria,
      );
      return prods.map(normalizar).slice(0, limit);
    } catch {
      return [];
    }
  }, []);

  // ── Carrito ───────────────────────────────────────────────
  const addToCart = useCallback(async (product, quantity = 1) => {
    setCartItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing)
        return prev.map((i) =>
          i.id === product.id ? { ...i, quantity: i.quantity + quantity } : i,
        );
      return [...prev, { ...product, quantity }];
    });
    return true;
  }, []);

  const removeFromCart = useCallback((id) => {
    setCartItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity <= 0) {
      setCartItems((prev) => prev.filter((i) => i.id !== id));
      return;
    }
    setCartItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity } : i)),
    );
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const getCartTotal = useCallback(
    () => cartItems.reduce((t, i) => t + parseFloat(i.price) * i.quantity, 0),
    [cartItems],
  );

  const getCartCount = useCallback(
    () => cartItems.reduce((c, i) => c + i.quantity, 0),
    [cartItems],
  );

  const goToCheckout = useCallback(() => {
    window.location.href = "/checkout";
  }, []);

  const testConnection = useCallback(async () => {
    const prods = await getProductos();
    return { ok: true, productsFound: prods.length };
  }, []);

  const submitOrder = useCallback(
    async (customerData) => {
      if (cartItems.length === 0) throw new Error("Carrito vacío");
      const total = cartItems.reduce(
        (t, i) => t + parseFloat(i.precio || i.price || 0) * i.quantity,
        0,
      );
      const orden = {
        orderId: String(Date.now()),
        cliente: customerData,
        productos: cartItems.map((i) => ({
          id: i.id,
          nombre: i.nombre || i.title || "",
          precio: parseFloat(i.precio || i.price || 0),
          cantidad: i.quantity,
          imagen: i.imagenes?.[0] || i.imagen || i.images?.[0]?.url || "",
        })),
        total,
        envio: "GRATIS",
        metodoPago: "Contra entrega",
      };
      const id = await guardarOrden(orden);
      clearCart();
      return { orderId: id, orden };
    },
    [cartItems, clearCart],
  );

  const value = {
    products,
    loading,
    error,
    cart: null,
    cartItems,
    fetchProducts,
    getProductByHandle,
    getCollectionProducts,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    goToCheckout,
    createCart: addToCart,
    testConnection,
    submitOrder,
  };

  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
};

// Normaliza un producto de Firebase al formato de la tienda
function normalizar(p) {
  return {
    id: p.id,
    title: p.nombre || p.title || "",
    description: p.descripcion || p.description || "",
    handle: p.id,
    availableForSale: p.disponible !== false,
    price: String(p.precio || p.price || "0"),
    precioOriginal: String(p.precioOriginal || p.precio || p.price || "0"),
    currencyCode: "USD",
    categoria: p.categoria || "todos",
    images: p.imagenes
      ? p.imagenes.map((url, i) => ({
          id: String(i),
          url,
          altText: p.nombre || "",
        }))
      : p.imagen
        ? [{ id: "0", url: p.imagen, altText: p.nombre || "" }]
        : [],
    variants: [
      {
        id: p.id + "_v1",
        title: "Default",
        availableForSale: true,
        price: String(p.precio || 0),
        currencyCode: "USD",
      },
    ],
    destacado: p.destacado || false,
    descuento: p.descuento || 0,
    stock: p.stock || 99,
  };
}

export { ShopifyProvider as StoreProvider };
export default StoreContext;
